import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { generateAndDownloadCsv } from '@/utils/downloadFile';
import { DeleteButton } from '@/components/DeleteConfirmDialog';
import { BarChart3, BarChart, Download, Plus, ArrowLeft, Edit2, ChevronRight } from 'lucide-react';

const ReportsPage = () => {
  const { API, user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';
  
  const [revenueByMonth, setRevenueByMonth] = useState([]);
  const [revenueByLocation, setRevenueByLocation] = useState([]);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showManualEntryDialog, setShowManualEntryDialog] = useState(false);
  const [standorte, setStandorte] = useState([]);
  const [manualEntry, setManualEntry] = useState({
    amount: '',
    location_id: '',
    note: ''
  });
  
  // P1: Detail View State
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationDetail, setLocationDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // P1: Edit Dialog State
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({ amount: '', note: '' });

  useEffect(() => {
    // Helfer dürfen keine Auswertungen sehen
    if (!isAdmin) {
      toast.error('Keine Berechtigung für Auswertungen');
      return;
    }
    fetchData();
    fetchStandorte();
  }, []);

  const fetchData = async () => {
    try {
      const [monthRes, locationRes, financialRes] = await Promise.all([
        axios.get(`${API}/reports/revenue-by-month`),
        axios.get(`${API}/reports/revenue-by-location`),
        axios.get(`${API}/automaten/financials/summary`),
      ]);
      setRevenueByMonth(monthRes.data || []);
      setRevenueByLocation(locationRes.data || { locations: [] });
      setFinancialSummary(financialRes.data || {});
    } catch (error) {
      // Nur bei echten Serverfehlern oder 403 einen Toast zeigen
      if (error.response?.status >= 500) {
        toast.error('Serverfehler beim Laden der Auswertungen');
      } else if (error.response?.status === 403) {
        toast.error('Keine Berechtigung für Auswertungen');
      }
      // Bei anderen Fehlern: Default-Werte setzen ohne Toast
      setRevenueByMonth([]);
      setRevenueByLocation({ locations: [] });
      setFinancialSummary({});
      console.error('Auswertungen laden:', error.response?.status || error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStandorte = async () => {
    try {
      const response = await axios.get(`${API}/standorte`);
      setStandorte(response.data || []);
    } catch (error) {
      setStandorte([]);
      console.error('Standorte laden:', error.response?.status || error.message);
    }
  };

  // P1: Fetch Location Detail
  const fetchLocationDetail = async (locationId) => {
    setLoadingDetail(true);
    try {
      const response = await axios.get(`${API}/reports/revenue-by-location/${locationId}`);
      setLocationDetail(response.data);
      setSelectedLocation(locationId);
    } catch (error) {
      toast.error('Fehler beim Laden der Standort-Details');
    } finally {
      setLoadingDetail(false);
    }
  };

  // P1: Edit Kassenbuch Entry
  const handleEditEntry = async () => {
    if (!editingEntry) return;
    
    try {
      await axios.put(`${API}/kassenbuch/${editingEntry.id}`, {
        amount: parseFloat(editForm.amount),
        note: editForm.note || null
      });
      toast.success('Eintrag aktualisiert');
      setEditingEntry(null);
      // Refresh detail view
      if (selectedLocation) {
        fetchLocationDetail(selectedLocation);
      }
      // Refresh overview data
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  // P1: Delete Kassenbuch Entry - verwendet DeleteConfirmDialog
  const handleDeleteEntry = async (entryId) => {
    try {
      await axios.delete(`${API}/kassenbuch/${entryId}`);
      toast.success('Eintrag gelöscht');
      // Refresh detail view
      if (selectedLocation) {
        fetchLocationDetail(selectedLocation);
      }
      // Refresh overview data
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setEditForm({
      amount: entry.amount?.toString() || '',
      note: entry.note || ''
    });
  };

  const handleManualEntry = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/kassenbuch/manual`, {
        amount: parseFloat(manualEntry.amount),
        location_id: manualEntry.location_id || null,
        note: manualEntry.note || null
      });
      toast.success('Umsatz eingetragen!');
      setShowManualEntryDialog(false);
      setManualEntry({ amount: '', location_id: '', note: '' });
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Eintragen');
    }
  };

  const exportMonthToCSV = () => {
    if (!revenueByMonth || revenueByMonth.length === 0) {
      toast.error('Keine Daten zum Exportieren');
      return;
    }
    
    const result = generateAndDownloadCsv(
      revenueByMonth,
      ['Monat', 'Umsatz'],
      ['_id', 'total'],
      `umsaetze_monat_${new Date().toISOString().split('T')[0]}.csv`
    );
    
    if (result.success) {
      toast.success('CSV-Datei heruntergeladen!');
    } else {
      toast.error(result.error || 'Fehler beim Exportieren');
    }
  };

  const exportLocationToCSV = () => {
    const data = revenueByLocation?.locations || [];
    if (data.length === 0) {
      toast.error('Keine Daten zum Exportieren');
      return;
    }
    
    const exportData = data.map(item => ({
      name: item.location?.name || 'Unbekannt',
      code: item.location?.code || '-',
      city: item.location?.city || '-',
      total: item.total || 0
    }));
    
    const result = generateAndDownloadCsv(
      exportData,
      ['Standort', 'Code', 'Stadt', 'Umsatz'],
      ['name', 'code', 'city', 'total'],
      `umsaetze_standort_${new Date().toISOString().split('T')[0]}.csv`
    );
    
    if (result.success) {
      toast.success('CSV-Datei heruntergeladen!');
    } else {
      toast.error(result.error || 'Fehler beim Exportieren');
    }
  };

  // Helfer: Keine Berechtigung
  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <p className="text-xl text-slate-600 mb-4">Keine Berechtigung</p>
        <p className="text-slate-500">Auswertungen sind nur für Administratoren verfügbar.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex justify-center items-center h-64">Laden...</div>;
  }

  // P1: Detail View für einzelnen Standort
  if (selectedLocation && locationDetail) {
    return (
      <div className="space-y-6 animate-slide-in" data-testid="location-detail-view">
        {/* Back Button & Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            onClick={() => { setSelectedLocation(null); setLocationDetail(null); }}
            className="flex items-center gap-2"
          >
            <ArrowLeft size={16} />
            Zurück
          </Button>
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
              Umsätze: {locationDetail.location?.name}
            </h1>
            <p className="text-slate-600 text-sm">
              {locationDetail.location?.code} • {locationDetail.location?.city}
            </p>
          </div>
        </div>

        {/* Summary Card */}
        <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-50">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-slate-500">Gesamtumsatz</p>
              <p className="text-3xl font-bold text-green-700">{locationDetail.total?.toFixed(2)} €</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Anzahl Auszählungen</p>
              <p className="text-3xl font-bold text-blue-700">{locationDetail.count}</p>
            </div>
          </div>
        </Card>

        {/* Entries Table */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Alle Umsätze dieses Standorts</h2>
          
          {loadingDetail ? (
            <p className="text-center py-8 text-slate-500">Laden...</p>
          ) : locationDetail.entries?.length === 0 ? (
            <p className="text-center py-8 text-slate-500">Keine Einträge gefunden</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="location-entries-table">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-semibold">Datum/Uhrzeit</th>
                    <th className="text-left py-3 px-4 font-semibold">Betrag</th>
                    <th className="text-left py-3 px-4 font-semibold">Quelle</th>
                    <th className="text-left py-3 px-4 font-semibold">Automat</th>
                    <th className="text-left py-3 px-4 font-semibold">Notiz</th>
                    <th className="text-right py-3 px-4 font-semibold">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {locationDetail.entries.map((entry) => (
                    <tr key={entry.id} className="border-b hover:bg-slate-50">
                      <td className="py-3 px-4">
                        {entry.created_at ? new Date(entry.created_at).toLocaleString('de-DE') : '-'}
                      </td>
                      <td className="py-3 px-4 font-medium text-green-700">
                        {entry.amount?.toFixed(2)} €
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">
                          {entry.type === 'auszaehlung' ? 'Auszählung' : entry.type}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-600">
                        {entry.automat?.code || '-'}
                      </td>
                      <td className="py-3 px-4 text-slate-500 text-sm max-w-xs truncate">
                        {entry.note || '-'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); openEditDialog(entry); }}
                            data-testid={`edit-entry-${entry.id}`}
                          >
                            <Edit2 size={14} />
                          </Button>
                          <DeleteButton
                            onDelete={() => handleDeleteEntry(entry.id)}
                            title="Umsatz löschen"
                            itemName={`${entry.amount?.toFixed(2)} €`}
                            description="Dieser Umsatz wird auch aus dem Kassenbuch gelöscht. Diese Aktion kann nicht rückgängig gemacht werden."
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Edit Dialog */}
        <Dialog open={!!editingEntry} onOpenChange={(open) => !open && setEditingEntry(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Umsatz bearbeiten</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Betrag (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editForm.amount}
                  onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Notiz</Label>
                <Textarea
                  value={editForm.note}
                  onChange={(e) => setEditForm({ ...editForm, note: e.target.value })}
                  placeholder="Zusätzliche Informationen..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingEntry(null)}>
                Abbrechen
              </Button>
              <Button onClick={handleEditEntry}>
                Speichern
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Main Reports View
  return (
    <div className="space-y-6 animate-slide-in" data-testid="reports-page">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            Auswertungen
          </h1>
          <p className="text-slate-600">Umsätze und Statistiken</p>
        </div>
        <Dialog open={showManualEntryDialog} onOpenChange={setShowManualEntryDialog}>
          <DialogTrigger asChild>
            <Button className="flex items-center space-x-2 bg-gradient-to-r from-green-500 to-emerald-600" data-testid="add-manual-revenue-button">
              <Plus size={20} />
              <span>Umsatz eintragen</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Manuellen Umsatz eintragen</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleManualEntry} className="space-y-4">
              <div>
                <Label>Betrag (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={manualEntry.amount}
                  onChange={(e) => setManualEntry({ ...manualEntry, amount: e.target.value })}
                  required
                  placeholder="0.00"
                  data-testid="manual-amount-input"
                />
              </div>
              <div>
                <Label>Standort (optional)</Label>
                <Select value={manualEntry.location_id || "none"} onValueChange={(value) => setManualEntry({ ...manualEntry, location_id: value === "none" ? "" : value })}>
                  <SelectTrigger data-testid="manual-location-select">
                    <SelectValue placeholder="Kein Standort (Sammelumsatz)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Kein Standort (Sammelumsatz)</SelectItem>
                    {standorte
                      .filter((standort) => standort.id && standort.id.trim() !== "")
                      .map((standort) => (
                        <SelectItem key={standort.id} value={standort.id}>
                          {standort.name} ({standort.code})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notiz (optional)</Label>
                <Textarea
                  value={manualEntry.note}
                  onChange={(e) => setManualEntry({ ...manualEntry, note: e.target.value })}
                  placeholder="Zusätzliche Informationen..."
                  rows={3}
                  data-testid="manual-note-input"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="submit-manual-revenue">
                Eintragen
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Financial Summary */}
      {financialSummary && financialSummary.automaten_count > 0 && (
        <Card className="p-6 bg-gradient-to-br from-purple-50 to-blue-50">
          <div className="flex items-center space-x-3 mb-6">
            <span className="text-3xl">💰</span>
            <div>
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
                Erwartete Finanzstatistik
              </h2>
              <p className="text-sm text-slate-600">
                Basierend auf {financialSummary.automaten_count} befüllte(n) Automat(en)
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <p className="text-sm text-slate-500 mb-2">Gesamtumsatz (erwartret)</p>
              <p className="text-3xl font-bold text-green-700">
                {financialSummary.total_expected_revenue.toFixed(2)} €
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <p className="text-sm text-slate-500 mb-2">Gesamtgewinn (erwartet)</p>
              <p className="text-3xl font-bold text-blue-700">
                {financialSummary.total_expected_profit.toFixed(2)} €
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <p className="text-sm text-slate-500 mb-2">Durchschnittliche Marge</p>
              <p className="text-3xl font-bold text-purple-700">
                {financialSummary.overall_margin_percentage.toFixed(1)}%
              </p>
            </div>
          </div>

          {/* Detailed Stats per Automat */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h3 className="font-bold text-lg mb-4">Pro Automat</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3">Automat</th>
                    <th className="text-right py-2 px-3">Artikel</th>
                    <th className="text-right py-2 px-3">Umsatz</th>
                    <th className="text-right py-2 px-3">Gewinn</th>
                    <th className="text-right py-2 px-3">Marge</th>
                  </tr>
                </thead>
                <tbody>
                  {financialSummary.detailed_stats.map((stat) => (
                    <tr key={stat.automat_id} className="border-b hover:bg-slate-50">
                      <td className="py-2 px-3 font-medium">{stat.automat_code}</td>
                      <td className="py-2 px-3 text-right text-slate-600">{stat.total_items}</td>
                      <td className="py-2 px-3 text-right font-medium text-green-700">
                        {stat.expected_revenue.toFixed(2)} €
                      </td>
                      <td className="py-2 px-3 text-right font-medium text-blue-700">
                        {stat.expected_profit.toFixed(2)} €
                      </td>
                      <td className="py-2 px-3 text-right font-medium text-purple-700">
                        {stat.margin_percentage.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-xs text-slate-500 mt-4">
            * Erwartete Werte basierend auf letzter Befüllung: Verkaufspreis × Füllmenge - Einkaufspreis × Füllmenge
          </p>
        </Card>
      )}

      {/* Revenue by Month */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <BarChart3 className="text-blue-600" size={28} />
            <div>
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
                Umsätze pro Monat
              </h2>
              <p className="text-sm text-slate-600">Monatliche Umsatzübersicht</p>
            </div>
          </div>
          <Button
            onClick={exportMonthToCSV}
            variant="outline"
            size="sm"
            className="flex items-center space-x-2"
            data-testid="export-month-button"
          >
            <Download size={16} />
            <span>CSV Export</span>
          </Button>
        </div>

        {revenueByMonth.length === 0 ? (
          <div className="text-center py-12">
            <BarChart className="mx-auto h-12 w-12 text-slate-300 mb-4" />
            <p className="text-lg font-medium text-slate-700 mb-2">Noch keine Umsatzdaten vorhanden</p>
            <p className="text-slate-500">Sobald Automaten ausgezählt werden, erscheinen hier die Auswertungen.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="revenue-month-table">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-semibold">Monat</th>
                  <th className="text-right py-3 px-4 font-semibold">Umsatz</th>
                </tr>
              </thead>
              <tbody>
                {revenueByMonth.map((item) => (
                  <tr key={item._id} className="border-b hover:bg-slate-50">
                    <td className="py-3 px-4">{item._id}</td>
                    <td className="py-3 px-4 text-right font-medium">
                      {item.total.toFixed(2)} €
                    </td>
                  </tr>
                ))}
                <tr className="bg-slate-100 font-bold">
                  <td className="py-3 px-4">Gesamt</td>
                  <td className="py-3 px-4 text-right">
                    {revenueByMonth.reduce((sum, item) => sum + item.total, 0).toFixed(2)} €
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Revenue by Location - P1 Updated with clickable rows */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <BarChart3 className="text-blue-600" size={28} />
            <div>
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
                Umsätze pro Standort
              </h2>
              <p className="text-sm text-slate-600">Aktive Standorte nach Umsatz • Klicken für Details</p>
            </div>
          </div>
          <Button
            onClick={exportLocationToCSV}
            variant="outline"
            size="sm"
            className="flex items-center space-x-2"
            data-testid="export-location-button"
          >
            <Download size={16} />
            <span>CSV Export</span>
          </Button>
        </div>

        {(!revenueByLocation?.locations || revenueByLocation.locations.length === 0) ? (
          <p className="text-slate-500 text-center py-8">Keine Standorte mit Umsätzen vorhanden</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="revenue-location-table">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-semibold">Standort</th>
                  <th className="text-left py-3 px-4 font-semibold">Code</th>
                  <th className="text-left py-3 px-4 font-semibold">Stadt</th>
                  <th className="text-right py-3 px-4 font-semibold">Umsatz</th>
                  <th className="text-right py-3 px-4 font-semibold w-12"></th>
                </tr>
              </thead>
              <tbody>
                {revenueByLocation.locations.map((item) => (
                  <tr 
                    key={item._id} 
                    className="border-b hover:bg-blue-50 cursor-pointer transition-colors"
                    onClick={() => fetchLocationDetail(item._id)}
                    data-testid={`location-row-${item._id}`}
                  >
                    <td className="py-3 px-4">
                      {item.location?.name || 'Unbekannt'}
                    </td>
                    <td className="py-3 px-4 text-slate-600">
                      {item.location?.code || '-'}
                    </td>
                    <td className="py-3 px-4 text-slate-600">
                      {item.location?.city || '-'}
                    </td>
                    <td className="py-3 px-4 text-right font-medium">
                      {(item.total || 0).toFixed(2)} €
                    </td>
                    <td className="py-3 px-4 text-right">
                      <ChevronRight size={16} className="text-slate-400" />
                    </td>
                  </tr>
                ))}
                <tr className="bg-slate-100 font-bold">
                  <td className="py-3 px-4" colSpan="3">Gesamt</td>
                  <td className="py-3 px-4 text-right">
                    {revenueByLocation.locations.reduce((sum, item) => sum + (item.total || 0), 0).toFixed(2)} €
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ReportsPage;
